import CancelButton from 'app/components/buttons/CancelButton'
import ConfirmationDialog from 'app/components/ConfirmationDialog'
import { usePlan } from 'commons/useSubscription'
import { isFreeTierOrFreeTrialPlanByPlanId } from 'commons/plan'
import PropTypes from 'prop-types'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import { Routes } from 'app/services/routeService/routeService'
import { Link } from '@material-ui/core'
import { Link as RouterLink } from 'react-router-dom'
import appConfigs from 'app/configs/appConfigs'
import CreateNotificationOverdraftFreePlanDialog from './CreateNotificationOverdraftFreePlanDialog'
import SecondaryButton from 'app/components/buttons/SecondaryButton'
import UpdatePlanButton from './UpdatePlanButton'
import { prop } from 'ramda'

function CreateNotificationOverdraftDialog(props) {
  const { isOpen, onClose, onAcceptOverdraft, submitErrorDetails, formValue } =
    props

  const { formatMessage } = useIntl()
  const plan = usePlan()
  const {
    isAllowedOverdraft,
    notesAvailable,
    totalCreditRequired,
    isExceedAllowedLimit,
    allowedOverdraftCount,
  } = submitErrorDetails?.payload || {}

  const toChangePlan = Routes.billingchangePlan()

  const handleUpdatePlanClick = () => {
    onClose()
  }

  // For negative balance we not allow overdraft for paid plan
  if (notesAvailable < 0) {
    return (
      <ConfirmationDialog
        open={isOpen}
        title={formatMessage({
          id: 'createNotification.dialog.overdraft.dialog.title',
        })}
        body={
          <div>
            <FormattedMessage
              id="createNotification.dialog.freeTrial.notesAvailable.overLimit.detail"
              values={{
                totalCreditRequired,
                notesAvailable,
                b: (text) => <b>{text}</b>,
                m1: (text) => <div className="mt-24">{text}</div>,
              }}
            />
          </div>
        }
        CancelButton={
          <SecondaryButton labelId="action.cancel" onClick={onClose} />
        }
        ConfirmButton={
          <UpdatePlanButton
            toChangePlan={toChangePlan}
            handleUpdatePlanClick={handleUpdatePlanClick}
          />
        }
      />
    )
  }

  // For free tier or free trial plan
  if (isFreeTierOrFreeTrialPlanByPlanId(prop('id', plan))) {
    return (
      <CreateNotificationOverdraftFreePlanDialog
        isOpen={isOpen}
        onClose={onClose}
        notesAvailable={notesAvailable}
        totalCreditRequired={totalCreditRequired}
        toChangePlan={toChangePlan}
        handleUpdatePlanClick={handleUpdatePlanClick}
      />
    )
  }

  // For paid plan
  // 1. If overdraft is allowed and is exceed allowed limit
  if (isExceedAllowedLimit) {
    return (
      <ConfirmationDialog
        open={isOpen}
        title={formatMessage({
          id: 'createNotification.dialog.overdraft.dialog.overdraft.limit.title',
        })}
        body={
          <div>
            <FormattedMessage
              id="createNotification.dialog.overdraft.dialog.overdraft.limit.body1"
              values={{
                limitPercent: appConfigs.limitOverdraftPercent,
                planName: prop('name', plan),
                planNotesAvailable: allowedOverdraftCount,
                b: (text) => <b>{text}</b>,
              }}
            />
            <div className="mt-10">
              <FormattedMessage id="createNotification.dialog.overdraft.dialog.overdraft.limit.body2" />
            </div>
            <div className="mt-10">
              <FormattedMessage id="createNotification.dialog.overdraft.dialog.overdraft.limit.body3" />
            </div>
          </div>
        }
        CancelButton={
          <SecondaryButton
            labelId="createNotification.dialog.overdraftNotice.dialog.later"
            onClick={onClose}
          />
        }
        ConfirmButton={
          <UpdatePlanButton
            toChangePlan={toChangePlan}
            handleUpdatePlanClick={handleUpdatePlanClick}
          />
        }
      />
    )
  }

  // 2. If overdraft is allowed and is not exceed allowed limit
  // 3. If overdraft is not allowed
  return (
    <ConfirmationDialog
      open={isOpen}
      title={formatMessage({
        id: !isAllowedOverdraft
          ? 'createNotification.dialog.overdraft.available.dialog.title'
          : 'createNotification.dialog.overdraft.dialog.title',
      })}
      body={
        !isAllowedOverdraft ? (
          <div>
            <FormattedMessage
              id="createNotification.dialog.overdraft.oneTime.detail1"
              values={{
                b: (text) => <b>{text}</b>,
              }}
            />
            <div className="mt-10">
              <FormattedMessage id="createNotification.dialog.overdraft.oneTime.detail2" />
            </div>
          </div>
        ) : (
          <div>
            <FormattedMessage
              id={'createNotification.dialog.overdraftFirst.dialog.detail'}
              values={{
                totalCreditRequired,
                notesAvailable,
                b: (text) => <b>{text}</b>,
                m1: (text) => <div className="mt-24">{text}</div>,
              }}
            />
          </div>
        )
      }
      CancelButton={
        <CancelButton
          labelId={
            !isAllowedOverdraft
              ? 'createNotification.dialog.overdraftNotice.dialog.later'
              : 'action.cancel'
          }
          onClick={onClose}
        />
      }
      ConfirmButton={
        !isAllowedOverdraft ? (
          <PrimaryButton color="secondary" variant="contained">
            <Link
              component={RouterLink}
              to={toChangePlan}
              className="text-white no-underline"
            >
              <FormattedMessage
                id={'createNotification.dialog.overdraftNotice.dialog.upgrade'}
              />
            </Link>
          </PrimaryButton>
        ) : (
          <PrimaryButton
            color="secondary"
            variant="contained"
            onClick={() => onAcceptOverdraft(formValue)}
          >
            <FormattedMessage id="createNotification.dialog.submit" />
          </PrimaryButton>
        )
      }
    />
  )
}

CreateNotificationOverdraftDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAcceptOverdraft: PropTypes.func.isRequired,
  formValue: PropTypes.object,
  submitErrorDetails: PropTypes.object,
}

export default CreateNotificationOverdraftDialog
